import type {
  SecretId,
  SecretListing,
  SecretPatchPayload,
  SecretPostPayload,
} from "@repo/app-types/entities/secret";
import {
  getTagsInvalidatedByCreate,
  getTagsInvalidatedByDelete,
  getTagsInvalidatedByUpdate,
  getTagsProvidedByList,
  TagType,
} from "../helpers/tags.js";
import { api } from "../redux.js";

/** Encrypted company-level secrets that other resources can reference. */
const secretApi = api.injectEndpoints({
  endpoints: (builder) => ({
    /** The company's secrets, newest first. */
    listSecrets: builder.query<
      SecretListing[],
      void,
      { data: { secrets: SecretListing[] } }
    >({
      query: () => "/secrets",
      transformResponse: (response) => response.data.secrets,
      providesTags: (result) => getTagsProvidedByList(TagType.Secret, result),
    }),
    createSecret: builder.mutation<
      SecretListing,
      SecretPostPayload,
      { data: { secret: SecretListing } }
    >({
      query: (payload) => ({
        url: "/secrets",
        method: "POST",
        body: payload,
      }),
      transformResponse: (response) => response.data.secret,
      invalidatesTags: getTagsInvalidatedByCreate(TagType.Secret),
    }),
    updateSecret: builder.mutation<
      SecretListing,
      { secretId: SecretId; payload: SecretPatchPayload },
      { data: { secret: SecretListing } }
    >({
      query: ({ secretId, payload }) => ({
        url: `/secrets/${secretId}`,
        method: "PATCH",
        body: payload,
      }),
      transformResponse: (response) => response.data.secret,
      invalidatesTags: (result, error, { secretId }) =>
        getTagsInvalidatedByUpdate(TagType.Secret, secretId),
    }),
    // The secret's tag also refetches the tools that reference it.
    deleteSecret: builder.mutation<void, SecretId>({
      query: (secretId) => ({
        url: `/secrets/${secretId}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, secretId) =>
        getTagsInvalidatedByDelete(TagType.Secret, secretId),
    }),
  }),
});

export const {
  useListSecretsQuery,
  useCreateSecretMutation,
  useUpdateSecretMutation,
  useDeleteSecretMutation,
} = secretApi;
